import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { logCookieConsentAPI } from '../api/api';
import '../css/CookieConsentBanner.css';

const CookieConsentBanner = () => {
  const t = useTranslation();
  const [visible, setVisible] = useState(false);

  // Helper to load/unload external Google Fonts dynamically
  const toggleGoogleFonts = (load) => {
    const fontId = 'dynamic-google-fonts';
    let link = document.getElementById(fontId);

    if (load) {
      if (!link) {
        link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        link.href =
          'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap';
        document.head.appendChild(link);
      }
    } else {
      if (link) {
        link.remove();
      }
    }
  };

  useEffect(() => {
    // Check if consent has been recorded in localStorage
    try {
      const savedConsent = localStorage.getItem('rocketbpm_cookie_consent');
      if (savedConsent) {
        const parsed = JSON.parse(savedConsent);
        const twelveMonths = 12 * 30 * 24 * 60 * 60 * 1000; // in ms
        const isExpired = Date.now() - parsed.timestamp > twelveMonths;

        if (!isExpired) {
          // Consent is valid; apply fonts if functional is accepted
          toggleGoogleFonts(!!parsed.functional);
          window.dispatchEvent(new CustomEvent('cookie-consent-updated'));
          return;
        }
      }
      // If no consent or expired, show banner
      setVisible(true);
    } catch (e) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    // Listen for custom event from Footer to reopen the banner
    const handleReopen = () => {
      setVisible(true);
    };

    window.addEventListener('open-cookie-banner', handleReopen);
    return () => {
      window.removeEventListener('open-cookie-banner', handleReopen);
    };
  }, []);

  const handleConsent = async (acceptAll) => {
    const consent = {
      essential: true,
      functional: acceptAll,
      timestamp: Date.now(),
    };

    // Save locally
    try {
      localStorage.setItem('rocketbpm_cookie_consent', JSON.stringify(consent));
    } catch (e) {}

    // Apply or remove Google Fonts based on choice
    toggleGoogleFonts(acceptAll);

    // Hide banner
    setVisible(false);

    // Dispatch event so other components (App.js, Login.js, Dashboard.js) update
    window.dispatchEvent(new CustomEvent('cookie-consent-updated'));

    // Write to audit log backend API
    try {
      await logCookieConsentAPI({
        essential: true,
        functional: acceptAll,
      });
    } catch (err) {
      // Fail silently for network issues to avoid blocking the user experience
    }
  };

  if (!visible) return null;

  // Multi-lingual fallback texts
  const title =
    t('cookie_banner_title') !== 'cookie_banner_title'
      ? t('cookie_banner_title')
      : 'Cookie Settings';

  const text =
    t('cookie_banner_text') !== 'cookie_banner_text'
      ? t('cookie_banner_text')
      : 'We value your privacy. We use cookies to improve your browsing experience and serve external resources like fonts. Please choose your preference.';

  const btnEssential =
    t('cookie_banner_accept_essential') !== 'cookie_banner_accept_essential'
      ? t('cookie_banner_accept_essential')
      : 'Accept only essential';

  const btnAcceptAll =
    t('cookie_banner_accept_all') !== 'cookie_banner_accept_all'
      ? t('cookie_banner_accept_all')
      : 'Accept all';

  return (
    <div className="cookie-banner-overlay">
      <div className="overlay_box">
        <div
          className="cookie-banner-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-title"
        >
          <h3 id="cookie-title">{title}</h3>
          <p>{text}</p>
          <div className="cookie-banner-actions">
            <button
              className="cookie-btn cookie-btn-essential"
              onClick={() => handleConsent(false)}
            >
              {btnEssential}
            </button>
            <button className="cookie-btn cookie-btn-accept" onClick={() => handleConsent(true)}>
              {btnAcceptAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
